/**
 * <AcceptInvitation /> — Task 17 / #49.
 *
 * Public landing page reached by clicking the link in a tenant-admin
 * invitation email. The link looks like:
 *
 *   ${appUrl}/invitations/accept?invite=<base64url(JSON.stringify({u,t,k}))>
 *
 * The page:
 *   - Parses the `invite` query param (pure helper) to confirm it's
 *     a well-formed blob; renders an error view if it's not.
 *   - Renders Accept / Decline buttons.
 *   - On Accept: POSTs the blob to `/memberships/accept`. On success,
 *       - if the user is already logged in, calls `refreshSession()`
 *         so the new membership appears, then navigates home.
 *       - if logged out, stashes a return URL under
 *         `postLoginRedirect` and sends them to `/login`. After
 *         login, LoginPage honors the return URL and we land back
 *         here authenticated.
 *   - On Decline: POSTs to `/memberships/decline` and renders a
 *     "Invitation declined" confirmation. Logged-out users are NOT
 *     redirected to login (declining is the terminal step).
 *
 * User-facing strings are localized via react-i18next under the
 * `acceptInvitation.*` keys in the `common` namespace (Task 18).
 */
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../services/api'
import {
  parseInvitePayload,
  buildPostLoginReturnUrl,
  POST_LOGIN_REDIRECT_KEY,
} from './acceptInvitationHelpers'
import styles from './AcceptInvitation.module.scss'

export function AcceptInvitation() {
  const { t } = useTranslation('common')
  const [searchParams] = useSearchParams()
  const inviteParam = searchParams.get('invite')
  const { user, refreshSession } = useAuth()
  const navigate = useNavigate()

  // phase: 'idle' | 'accepting' | 'declining' | 'accepted' | 'declined' | 'error'
  const [phase, setPhase] = useState('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [resultTenantName, setResultTenantName] = useState('')

  // Parse once per inviteParam — pure and cheap, but the memo keeps
  // referential stability for any future effects we add.
  const payload = useMemo(() => parseInvitePayload(inviteParam), [inviteParam])

  // Malformed / missing invite → terminal error view, no buttons.
  if (!payload) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h1 className={styles.title}>{t('acceptInvitation.title')}</h1>
          <p className={styles.error} role="alert">
            {t('acceptInvitation.invalidLink')}
          </p>
        </div>
      </div>
    )
  }

  const handleAccept = async () => {
    setPhase('accepting')
    setErrorMessage('')
    try {
      const res = await api.post('/memberships/accept', { invite: inviteParam })
      const tenantName = res?.data?.tenantName || ''
      setResultTenantName(tenantName)

      if (user) {
        // Logged-in path: the accept call flipped the membership to
        // active, but the pre-token-gen trigger picks `lastActiveTenantId`
        // when re-issuing claims — which is still pointing at the user's
        // previous tenant. Switch the active tenant first so the next
        // token refresh lands the user in the tenant they just accepted.
        try {
          await api.put('/me/active-tenant', { tenantId: payload.t })
        } catch (err) {
          // Non-fatal: the accept itself succeeded. The user can still
          // switch tenants manually via the TenantSwitcher after landing.
          // eslint-disable-next-line no-console
          console.warn('Could not switch active tenant after accept:', err)
        }
        // Then refresh so the new membership + activeTenantId claim
        // both land in AuthContext.
        const r = await refreshSession()
        if (!r?.success) {
          setPhase('error')
          setErrorMessage(t('acceptInvitation.refreshFailed'))
          return
        }
        setPhase('accepted')
        // A short delay lets the success message render before we leave.
        setTimeout(() => navigate('/'), 600)
      } else {
        // Logged-out path: stash a return URL so LoginPage can bring
        // the user back here after they authenticate, then send them
        // to /login. Note: the backend has already marked the
        // membership as active at this point — the return trip is
        // purely so the UI reflects it.
        setPhase('accepted')
        const returnUrl = buildPostLoginReturnUrl(inviteParam)
        if (returnUrl) {
          try {
            localStorage.setItem(POST_LOGIN_REDIRECT_KEY, returnUrl)
          } catch {
            // Sandboxed storage; the user can still log in and find
            // the tenant in their list, so this is non-fatal.
          }
        }
        setTimeout(() => navigate('/login'), 600)
      }
    } catch {
      // Backend returns a deliberately uniform "invalid or already
      // used" error — surface the same wording, no leakage of
      // server-side detail.
      setPhase('error')
      setErrorMessage(t('acceptInvitation.genericError'))
    }
  }

  const handleDecline = async () => {
    setPhase('declining')
    setErrorMessage('')
    try {
      await api.post('/memberships/decline', { invite: inviteParam })
      setPhase('declined')
    } catch {
      setPhase('error')
      setErrorMessage(t('acceptInvitation.genericError'))
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t('acceptInvitation.title')}</h1>
        <p className={styles.intro}>{t('acceptInvitation.intro')}</p>

        {phase === 'idle' && (
          <div className={styles.actions}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={handleAccept}
            >
              {t('acceptInvitation.acceptCta')}
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={handleDecline}
            >
              {t('acceptInvitation.declineCta')}
            </button>
          </div>
        )}

        {phase === 'accepting' && (
          <p className={styles.status} role="status">
            {t('acceptInvitation.accepting')}
          </p>
        )}

        {phase === 'declining' && (
          <p className={styles.status} role="status">
            {t('acceptInvitation.declining')}
          </p>
        )}

        {phase === 'accepted' && (
          <p className={styles.success} role="status">
            {user
              ? resultTenantName
                ? t('acceptInvitation.successJoined', { tenantName: resultTenantName })
                : t('acceptInvitation.successJoinedNoName')
              : t('acceptInvitation.acceptSuccessLoggedOut')}
            <br />
            <span className={styles.loadingNote}>{t('acceptInvitation.loadingNote')}</span>
          </p>
        )}

        {phase === 'declined' && (
          <p className={styles.success} role="status">
            {t('acceptInvitation.declineSuccess')}
          </p>
        )}

        {phase === 'error' && (
          <p className={styles.error} role="alert">
            {errorMessage || t('acceptInvitation.genericError')}
          </p>
        )}
      </div>
    </div>
  )
}

export default AcceptInvitation
